/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { currencyFormatter, formatStockQuantity } from '../application/inventoryUtils';
import {
  formatStockExpiryDate,
  getInventoryStockStatusLabel,
  getInventoryStockStatusTone,
  isStockExpiryPast,
} from '../application/inventoryStockStatusPresentation';
import type { StockRow } from '../../inventory/stock/public-api';
import { getStockLotExpirySplit, isUnstockedStockRow, listVariantStockCells, type VariantStockCellRow } from '../../inventory/stock/public-api';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { ModalShell } from '../../shared/modals/ModalShell'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { StatusPill } from '../../shared/surface-cards/StatusPill'
import type { StatusPillTone } from '../../shared/surface-cards/StatusPill';

type Props = {
  visible: boolean;
  stock: StockRow | null;
  canEdit: boolean;
  canAdjust?: boolean;
  canTransfer?: boolean;
  canViewValuation?: boolean;
  onClose: () => void;
  onIngress: (row: StockRow) => void;
  onAdjust: (row: StockRow) => void;
  /** #1497: baja explícita de unidades vencidas (abre el ajuste precargado). */
  onWriteOffExpired?: (row: StockRow) => void;
  onCorrectCost: (row: StockRow) => void;
  onTransfer: (row: StockRow) => void;
  onHistory: (row: StockRow) => void;
  layoutWide?: boolean;
  /** #709: si la capability product_variants está activa, muestra celdas de variante. */
  variantsEnabled?: boolean;
};

function toStatusPillTone(tone: ReturnType<typeof getInventoryStockStatusTone>): StatusPillTone {
  if (tone === 'critical') return 'danger';
  if (tone === 'warning') return 'warning';
  return 'success';
}

function DetailKv({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <View style={modalExperience.detailKvRow}>
      <Text style={modalExperience.detailMetaLabel}>{label}</Text>
      <Text
        selectable
        style={[modalExperience.detailKvValueStrong, danger ? modalExperience.detailMetaValueDanger : null]}
      >
        {value}
      </Text>
    </View>
  );
}

export const StockDetailModal = ({
  visible,
  stock,
  canEdit,
  canAdjust = canEdit,
  canTransfer = canEdit,
  canViewValuation = true,
  onClose,
  onIngress,
  onAdjust,
  onWriteOffExpired,
  onCorrectCost,
  onTransfer,
  onHistory,
  layoutWide = false,
  variantsEnabled = false,
}: Props) => {
  const [variantCells, setVariantCells] = useState<VariantStockCellRow[]>([]);
  const { width: winW } = useWindowDimensions();
  const modalCardMaxWidth = layoutWide ? Math.min(720, winW - 48) : Math.min(520, winW - 32);
  const salePrice = stock?.productPrice ?? null;
  // Fila sintética (catálogo sin fila en stocks): solo «Ingresar» aplica —
  // Ajustar/Corregir/Transferir operan sobre filas reales y fallarían.
  const unstocked = stock ? isUnstockedStockRow(stock) : false;
  const grossMarginValue =
    canViewValuation && stock && salePrice != null ? salePrice - stock.avgCost : null;
  const grossMarginPercent =
    salePrice != null && salePrice > 0 && grossMarginValue != null ? (grossMarginValue / salePrice) * 100 : null;
  const displayBarcode = stock?.productBarcode ?? stock?.productSku ?? null;
  const displaySku =
    stock?.productBarcode && stock.productSku && stock.productBarcode !== stock.productSku ? stock.productSku : null;

  const runAndClose = (action: (row: StockRow) => void) => {
    if (!stock) return;
    onClose();
    action(stock);
  };

  const statusTone = stock ? getInventoryStockStatusTone(stock) : 'ok';
  const statusLabel = stock ? getInventoryStockStatusLabel(stock) : null;
  const showStatusPill = statusLabel != null && statusTone !== 'ok';
  const showCriticalHint = statusTone === 'critical';

  // Lotes FEFO a nivel producto: las filas de variante no tienen lotes propios.
  const lotSplit =
    stock && stock.variantId == null
      ? getStockLotExpirySplit(stock.productId, stock.branchId)
      : null;

  const metaParts: string[] = [];
  if (stock) {
    metaParts.push(`Mínimo ${formatStockQuantity(stock.minQuantity, stock.unitOfMeasure)}`);
    if (stock.expiresAt) {
      metaParts.push(
        isStockExpiryPast(stock.expiresAt)
          ? `Venció el ${formatStockExpiryDate(stock.expiresAt)}`
          : `Vence ${formatStockExpiryDate(stock.expiresAt)}`
      );
    }
    if (lotSplit && lotSplit.expiredQuantity > 0) {
      metaParts.push(
        `${formatStockQuantity(lotSplit.sellableQuantity, stock.unitOfMeasure)} vendibles · ` +
          `${formatStockQuantity(lotSplit.expiredQuantity, stock.unitOfMeasure)} vencidas`
      );
    }
  }

  useEffect(() => {
    if (!visible || !stock || !variantsEnabled) {
      setVariantCells([]);
      return;
    }
    void listVariantStockCells(stock.branchId, stock.productId).then(setVariantCells);
  }, [visible, stock?.branchId, stock?.productId, variantsEnabled]);

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      appearance="ticket"
      kicker="Stock"
      title="Detalle de stock"
      keyboardAvoiding={false}
      widthVariant="comfortable"
      cardStyle={
        layoutWide
          ? { maxWidth: modalCardMaxWidth, gap: 0, overflow: 'hidden', padding: 0 }
          : { gap: 0, overflow: 'hidden', padding: 0 }
      }
      scrollStyle={styles.detailScroll}
      scrollContentStyle={styles.detailScrollContent}
      footer={
        stock ? (
          <View style={styles.actionsDock}>
            <View style={modalExperience.detailActionsGrid}>
              <TouchableOpacity
                style={[
                  modalExperience.detailActionBtn,
                  modalExperience.detailActionBtnPrimary,
                  !canEdit && posUi.disabledCard,
                ]}
                onPress={() => runAndClose(onIngress)}
                disabled={!canEdit}
                accessibilityRole="button"
                accessibilityLabel="Ingresar stock"
              >
                <Ionicons name="add-circle-outline" size={18} color="#FFFFFF" />
                <Text style={modalExperience.detailActionTextPrimary}>Ingresar</Text>
              </TouchableOpacity>
              {!unstocked ? (
                <>
                  <TouchableOpacity
                    style={[modalExperience.detailActionBtn, !canAdjust && posUi.disabledCard]}
                    onPress={() => runAndClose(onAdjust)}
                    disabled={!canAdjust}
                    accessibilityRole="button"
                    accessibilityLabel="Ajustar stock"
                    testID="stock.adjust"
                  >
                    <Ionicons name="create-outline" size={18} color={ticketColors.ink} />
                    <Text style={modalExperience.detailActionText}>Ajustar</Text>
                  </TouchableOpacity>
                  {canViewValuation ? (
                    <TouchableOpacity
                      style={[modalExperience.detailActionBtn, !canEdit && posUi.disabledCard]}
                      onPress={() => runAndClose(onCorrectCost)}
                      disabled={!canEdit}
                      accessibilityRole="button"
                      accessibilityLabel="Corregir costo"
                      testID="stock.correct-cost"
                    >
                      <Ionicons name="pricetag-outline" size={18} color={ticketColors.ink} />
                      <Text style={modalExperience.detailActionText}>Corregir costo</Text>
                    </TouchableOpacity>
                  ) : null}
                  <TouchableOpacity
                    style={[modalExperience.detailActionBtn, !canTransfer && posUi.disabledCard]}
                    onPress={() => runAndClose(onTransfer)}
                    disabled={!canTransfer}
                    accessibilityRole="button"
                    accessibilityLabel="Transferir stock"
                    testID="stock.transfer"
                  >
                    <Ionicons name="swap-horizontal-outline" size={18} color={ticketColors.ink} />
                    <Text style={modalExperience.detailActionText}>Transferir</Text>
                  </TouchableOpacity>
                </>
              ) : null}
              <TouchableOpacity
                style={modalExperience.detailActionBtn}
                onPress={() => runAndClose(onHistory)}
                accessibilityRole="button"
                accessibilityLabel="Ver historial de movimientos"
                testID="stock.history"
              >
                <Ionicons name="time-outline" size={18} color={ticketColors.ink} />
                <Text style={modalExperience.detailActionText}>Historial</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : null
      }
    >
      {stock ? (
        <>
          <View style={modalExperience.detailMetricHero}>
            <View style={modalExperience.detailMetricMain}>
              <Text style={styles.productName} numberOfLines={2} ellipsizeMode="tail">
                {stock.productName}
              </Text>
              <Text style={modalExperience.detailMetricValue}>
                {formatStockQuantity(stock.quantity, stock.unitOfMeasure)}
              </Text>
              {metaParts.length > 0 ? (
                <Text style={modalExperience.detailMetricMeta}>{metaParts.join(' · ')}</Text>
              ) : null}
            </View>
            {showStatusPill ? (
              <StatusPill label={statusLabel!} tone={toStatusPillTone(statusTone)} compact />
            ) : null}
          </View>

          {showCriticalHint ? (
            <Text style={styles.criticalHint}>
              Registrá un ingreso o ajuste para corregir la diferencia.
            </Text>
          ) : null}

          {lotSplit && lotSplit.expiredQuantity > 0 && canAdjust && onWriteOffExpired ? (
            <View style={styles.expiredBanner}>
              <View style={styles.expiredBannerText}>
                <Ionicons name="warning-outline" size={16} color={ticketColors.warn} />
                <Text style={styles.expiredBannerCopy}>
                  {formatStockQuantity(Math.min(lotSplit.expiredQuantity, Math.max(stock.quantity, 0)), stock.unitOfMeasure)}{' '}
                  vencida{lotSplit.expiredQuantity === 1 ? '' : 's'} sin dar de baja
                </Text>
              </View>
              <TouchableOpacity
                style={styles.expiredWriteOffBtn}
                onPress={() => runAndClose(onWriteOffExpired)}
                accessibilityRole="button"
                accessibilityLabel="Dar de baja unidades vencidas"
                testID="stock.writeOffExpired"
              >
                <Text style={styles.expiredWriteOffBtnText}>Dar de baja</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          {variantCells.length > 0 ? (
            <>
              <Text style={modalExperience.formSectionTitle}>Stock por variante</Text>
              <View style={styles.kvBlock}>
                {variantCells.map((cell) => (
                  <DetailKv
                    key={cell.variantId}
                    label={cell.label}
                    value={formatStockQuantity(cell.quantity, stock.unitOfMeasure)}
                  />
                ))}
              </View>
            </>
          ) : null}

          {lotSplit && lotSplit.lots.length > 0 ? (
            <>
              <Text style={modalExperience.formSectionTitle}>Lotes</Text>
              <View style={styles.kvBlock}>
                {lotSplit.lots.map((lot) => (
                  <DetailKv
                    key={lot.lotId}
                    label={
                      lot.expiresAt == null
                        ? 'Sin vencimiento'
                        : lot.expired
                          ? `Venció el ${formatStockExpiryDate(lot.expiresAt)}`
                          : `Vence ${formatStockExpiryDate(lot.expiresAt)}`
                    }
                    value={formatStockQuantity(lot.remainingQuantity, stock.unitOfMeasure)}
                    danger={lot.expired}
                  />
                ))}
              </View>
            </>
          ) : null}

          <Text style={modalExperience.formSectionTitle}>Datos</Text>
          <View style={styles.kvBlock}>
            <DetailKv label="Código" value={displayBarcode ?? 'Sin código'} />
            {displaySku ? <DetailKv label="SKU" value={displaySku} /> : null}
            {stock.productBrand ? <DetailKv label="Marca" value={stock.productBrand} /> : null}
            {stock.productPreferredSupplier ? (
              <DetailKv label="Proveedor" value={stock.productPreferredSupplier} />
            ) : null}
            {stock.productSupplierItemCode ? (
              <DetailKv label="Código proveedor" value={stock.productSupplierItemCode} />
            ) : null}
          </View>

          {canViewValuation ? (
            <>
              <Text style={modalExperience.formSectionTitle}>Costo y margen</Text>
              <View style={styles.kvBlock}>
                {salePrice != null ? (
                  <>
                    <DetailKv label="Precio venta" value={currencyFormatter.format(salePrice)} />
                    <DetailKv label="Costo promedio" value={currencyFormatter.format(stock.avgCost)} />
                    <DetailKv
                      label="Margen bruto"
                      value={[
                        grossMarginValue != null ? currencyFormatter.format(grossMarginValue) : '—',
                        grossMarginPercent != null ? `(${grossMarginPercent.toFixed(1)}%)` : null,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      danger={grossMarginValue != null && grossMarginValue < 0}
                    />
                  </>
                ) : (
                  <Text style={modalExperience.detailInfoLine}>
                    Sin precio en catálogo. Cargalo para ver el margen.
                  </Text>
                )}
              </View>
            </>
          ) : null}
        </>
      ) : null}
    </ModalShell>
  );
};

const styles = StyleSheet.create({
  detailScroll: {
    flexShrink: 1,
  },
  detailScrollContent: {
    padding: spacing.md,
    paddingTop: spacing.md,
    gap: spacing.sm,
    paddingBottom: spacing.sm,
  },
  productName: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink2,
    lineHeight: 18,
  },
  criticalHint: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '600',
    color: ticketColors.bad,
  },
  expiredBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.warn,
    borderRadius: 10,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    backgroundColor: ticketColors.paper2 ?? ticketColors.paper,
  },
  expiredBannerText: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 1,
  },
  expiredBannerCopy: {
    fontSize: 13,
    lineHeight: 17,
    fontWeight: '600',
    color: ticketColors.ink2,
    flexShrink: 1,
  },
  expiredWriteOffBtn: {
    paddingVertical: 6,
    paddingHorizontal: spacing.sm,
    borderRadius: 8,
    backgroundColor: ticketColors.warn,
  },
  expiredWriteOffBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  kvBlock: {
    gap: 2,
    marginBottom: spacing.xs,
  },
  actionsDock: {
    flexShrink: 0,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
    padding: spacing.md,
    paddingTop: spacing.sm,
  },
});
